import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Download } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import type { DesktopRequiredPayload } from '../../../contracts/desktop-required.types';

export interface DesktopRequiredBlockProps {
  payload: DesktopRequiredPayload;
  downloadHref: string;
  className?: string;
}

/**
 * Typed computer-use miss. Interactive chats can continue after the
 * clinician opens desktop; schedule/event fires already failed.
 */
export function DesktopRequiredBlock({
  payload,
  downloadHref,
  className,
}: DesktopRequiredBlockProps) {
  const isScheduledMiss = payload.trigger === 'schedule' || payload.trigger === 'event';

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-xl border border-border bg-surface-page px-4 py-3',
        className
      )}
      data-testid="desktop-required-block"
      data-trigger={payload.trigger}
    >
      <TypographyP2 className="text-text-secondary">
        {isScheduledMiss ? (
          <FormattedMessage
            defaultMessage="This scheduled run needed Heidi desktop connected. Opening the app later will not resume this step."
            id="Dgh0tGY1T+"
          />
        ) : (
          <FormattedMessage
            defaultMessage="Open Heidi desktop to continue this chat."
            id="2+a0gG/OPY"
          />
        )}
      </TypographyP2>
      <TypographyCaption className="text-text-secondary">
        {isScheduledMiss ? (
          <FormattedMessage
            defaultMessage="Install or reopen Heidi desktop so the next scheduled run can use it."
            id="BpqahaV76B"
          />
        ) : (
          <FormattedMessage
            defaultMessage="Download the app, open this project, and send again from desktop."
            id="k6ziUqvV4C"
          />
        )}
      </TypographyCaption>
      <Button size="sm" type="button" variant="default" className="w-fit" asChild>
        <a
          data-testid="desktop-required-download-link"
          href={downloadHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Download aria-hidden />
          <FormattedMessage defaultMessage="Get the desktop app" id="TvOD8VviD2" />
        </a>
      </Button>
    </div>
  );
}
